import * as React from 'react';
import {
    CreateButton,
    DateField,
    FilterButton,
    List,
    NumberField,
    SearchInput,
    TopToolbar,
    TextField,
    DatagridConfigurable,
    SelectColumnsButton,
    useTranslate,
    FunctionField,
    useNotify,
    WithListContext,
    DatagridLoading,
    Link,
    downloadCSV,
    useListContext,
    Pagination,
} from 'react-admin';
import { useMediaQuery, Theme, Typography } from '@mui/material';
import SchoolMergeButton from '../SchoolMergeButton';
import { Alert } from '@mui/material';

import CustomStatusField from '../../controls/CustomStatusField';
import PupilListAside from './PupilListAside';
import get from 'lodash/get';
import CustomPagination from '../../common/CustomPagination';
import SourceNameField from '../SourceNameField';
import SessionBadge from '../controls/SessionBadge';
import StaleBadge from '../controls/StaleBadge';
import ConditionalLoginField from '../ConditionalLoginField';
import CustomLogAsPupilButton from '../../controls/CustomLogAsPupilButton';
import CustomCopyAccessTokenButton from '../../controls/CustomCopyAccessTokenButton';
import SchoolNameField from '../SchoolNameField';
// import ProductFullNameField from '../product/ProductFullNameField';
// import SubscriberFullNameField from './SubscriberFullNameField';
import jsonExport from 'jsonexport/dist';
import CreateUserDialogButton from '../CreateUserDialogButton';
import BulkForceStaleButton from '../BulkForceStaleButton';
import { BulkDeleteButton } from 'react-admin';
import { IfCanAccess } from '@react-admin/ra-rbac';
import { useCanAccess } from '@react-admin/ra-rbac';
const pupilFilters = [<SearchInput source="q" alwaysOn />];

const PupilBulkActions = () => (
    <>
        <BulkForceStaleButton />
        <BulkDeleteButton />
    </>
);

const ListActions = () => {
    const { filterValues } = useListContext();
    const isSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('md'));
    return (
        <TopToolbar>
            {isSmall ? <FilterButton /> : null}

            {/* <CreateButton /> */}
            <SchoolMergeButton
                resource="pupil"
                school={filterValues?.parent_group}
            />
            <IfCanAccess action="create" resource="pupil">
                <CreateUserDialogButton resource="pupil" />
            </IfCanAccess>
            <SelectColumnsButton preferenceKey="pupil.datagrid.v2" />
            {/* <ListViewButton /> */}
        </TopToolbar>
    );
};

const PupilList = () => {
    const notify = useNotify();
    const { canAccess: canWritePupil } = useCanAccess({
        action: 'write',
        resource: 'pupil',
    });
    const handleOnClick = e => {
        e.stopPropagation();
        navigator.clipboard.writeText(e.target.innerText);
        //notify(e.target.innerText + ' kopioitu leikepöydälle.', { type: 'info' });
        notify(
            <Alert severity="info">
                {e.target.innerText} kopioitu leikepöydälle.
            </Alert>
        );
    };

    const isXsmall = useMediaQuery<Theme>(theme =>
        theme.breakpoints.down('sm')
    );
    const sortable = [
        'public.firstname',
        'public.lastname',
        'last_login',
        'registered',
        'services',
    ];

    const translate = useTranslate();

    const isSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('md'));

    const exporter = pupils => {
        const usersForExport = pupils.map(pupil => {
            const userForExport = {
                id: pupil.id,
                Etunimi: pupil.public.firstname,
                Sukunimi: pupil.public.lastname,
                Avainkoodi: pupil.public.code,
                'Kirjauduttu viimeksi': get(pupil, 'last_login', ''),
                Rekisteröity: pupil.registered,
                Oppilaitokset: pupil.schools
                    .map(school => school.name)
                    .join(', '),
                Mpass: pupil.services?.includes('mpass') ? 'Kyllä' : 'Ei',
                Lisenssit: pupil.licenses
                    .filter(
                        license =>
                            license.status === 0 &&
                            Date.parse(license.valid_until) > Date.now()
                    )
                    .map(license => license.name)
                    .join(', '),
            };
            return userForExport;
        });
        jsonExport(
            usersForExport,
            {
                headers: [
                    'id',
                    'Etunimi',
                    'Sukunimi',
                    'Avainkoodi',
                    'Mpass',
                    'Oppilaitokset',
                    'Kirjauduttu viimeksi',
                    'Rekisteröity',
                    'Lisenssit',
                ],
                rowDelimiter: ';',
            },
            (err, csv) => {
                if (err) {
                    console.error('Export error:', err);
                    notify(translate('notifications.EXPORT_ERROR'), {
                        type: 'error',
                    });
                    return;
                }
                // UTF-8 BOM so Excel renders Scandinavian letters correctly.
                downloadCSV(`\ufeff${csv}`, 'users'); // download as 'users.csv' file
            }
        );
    };

    return (
        <List
            filters={isSmall ? pupilFilters : undefined}
            sort={{ field: 'registered', order: 'DESC' }}
            filter={{
                need_count: true,
                sessions: true,
                licenses: false,
                stale_info: true,
            }}
            perPage={50}
            pagination={
                <Pagination rowsPerPageOptions={[10, 25, 50, 100, 200]} />
            }
            aside={<PupilListAside />}
            actions={<ListActions />}
            //pagination={false ? <CustomPagination /> : undefined}
            exporter={exporter}
        >
            <WithListContext
                render={({ isLoading, isFetching, data }) =>
                    isLoading || isFetching ? (
                        <DatagridLoading nbChildren={6} nbFakeLines={8} />
                    ) : (
                        <DatagridConfigurable
                            preferenceKey="pupil.datagrid.v2"
                            /*
                            rowSx={record => ({
                                color:
                                    record.status === 0
                                        ? 'none'
                                        : 'rgba(255, 0, 0, 0.05)',
                            })}
                            */
                            //optimized
                            omit={['guid', 'copy_access_token']}
                            rowClick="edit"
                            //rowClick={(id: any, basePath: any, record: any) => {window.open("https://oppimateriaalit-aspa.otava.fi/#/"+basePath+"/"+id, "_blank");return false;}}
                            bulkActionButtons={
                                canWritePupil ? <PupilBulkActions /> : false
                            }
                            sx={{
                                '& .column-groups': {
                                    md: { display: 'none' },
                                    lg: { display: 'table-cell' },
                                },
                            }}
                        >
                            {/* {columns} */}
                            <TextField
                                source="guid"
                                key="guid"
                                sortable={sortable.includes('guid')}
                                onClick={handleOnClick}
                            />
                            <TextField
                                source="public.firstname"
                                key="firstname"
                                sortable={sortable.includes('public.firstname')}
                                onClick={handleOnClick}
                            />
                            <TextField
                                source="public.lastname"
                                key="lastname"
                                sortable={sortable.includes('public.lastname')}
                                onClick={handleOnClick}
                            />
                            <StaleBadge>
                                <TextField
                                    source="public.code"
                                    key="code"
                                    sortable={sortable.includes('public.code')}
                                    onClick={handleOnClick}
                                />
                            </StaleBadge>
                            <SchoolNameField
                                source="school.title"
                                key="school"
                                sortable={sortable.includes('school')}
                            />
                            <SessionBadge
                                source="last_login"
                                options={{
                                    showTTL: false,
                                }}
                                sortByOrder="DESC"
                            >
                                <ConditionalLoginField
                                    showTime
                                    locales="fi-FI"
                                    sortable={sortable?.includes('last_login')} // Conditional sortable
                                    sortByOrder="DESC"
                                />
                            </SessionBadge>
                            <DateField
                                source="registered"
                                key="registered"
                                showTime={false}
                                locales="fi-FI"
                                sortable={sortable.includes('registered')}
                                sortByOrder="DESC"
                            />
                            <CustomLogAsPupilButton
                                label={translate(
                                    'actions.login_as.column_label'
                                )}
                            />
                            <CustomCopyAccessTokenButton
                                role="pupil"
                                source="copy_access_token"
                                label={translate(
                                    'actions.copy_access_token.column_label'
                                )}
                            />
                            <SourceNameField label="Lähde" size="small" />
                        </DatagridConfigurable>
                    )
                }
            />
        </List>
    );
};

export default PupilList;
